import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
    Fade,
    Backdrop,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';

const PSINotificationModal = ({ open, onClose }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [dontShowAgain, setDontShowAgain] = useState(false);

    // Parent component should handle checking localStorage

    const handleRemindLater = () => {
        // Just close the modal, it will show again on next login
        onClose();
    };

    const handleDontShowAgain = () => {
        // Set localStorage to remember user's choice
        localStorage.setItem('hidePSINotification', 'true');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleRemindLater}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            TransitionComponent={Fade}
            BackdropComponent={Backdrop}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(3px)',
                },
            }}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 3,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
                    overflow: 'hidden',
                },
            }}
        >
            {/* Header with gradient background */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #ff5e18 0%, #ff8f53 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleRemindLater}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <DialogTitle
                    sx={{
                        textAlign: 'center',
                        color: 'white',
                        pb: 2,
                        pt: 3,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': {
                                        boxShadow: '0 0 0 0 rgba(255,255,255,0.4)',
                                    },
                                    '70%': {
                                        boxShadow: '0 0 0 20px rgba(255,255,255,0)',
                                    },
                                    '100%': {
                                        boxShadow: '0 0 0 0 rgba(255,255,255,0)',
                                    },
                                },
                            }}
                        >
                            <WarningAmberIcon sx={{ fontSize: 48, color: 'white' }} />
                        </Box>
                        <Typography variant="h5" component="h2" fontWeight="bold">
                            Important PSI Testing Update
                        </Typography>
                    </Box>
                </DialogTitle>
            </Box>

            <DialogContent sx={{ pt: 4, pb: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            mb: 3,
                        }}
                    >
                        Testing at PSI? Please Call Us First!
                    </Typography>

                    <Box
                        sx={{
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: 2,
                            p: 3,
                            mb: 3,
                            border: `1px solid ${theme.palette.grey[200]}`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                            <PhoneInTalkIcon sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
                            <Typography variant="h4" fontWeight="bold" color="primary">
                                225-293-8107
                            </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary">
                            Our team has critical information about recent changes to PSI testing procedures that may affect your exam.
                        </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                        This is a temporary notification for all PSI test-takers. We want to ensure you have the best possible testing
                        experience.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 2,
                    px: 3,
                    pb: 3,
                    pt: 0,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleDontShowAgain}
                    startIcon={<NotificationsOffIcon />}
                    fullWidth={isMobile}
                    sx={{
                        borderColor: theme.palette.grey[400],
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            borderColor: theme.palette.grey[600],
                            backgroundColor: theme.palette.grey[50],
                        },
                    }}
                >
                    Don't Show Again
                </Button>
                <Button
                    variant="contained"
                    onClick={handleRemindLater}
                    startIcon={<AccessTimeIcon />}
                    fullWidth={isMobile}
                    sx={{
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                        boxShadow: '0 4px 12px rgba(255, 94, 24, 0.3)',
                    }}
                >
                    Remind Me Later
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PSINotificationModal;